import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Flex, Grid, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { DatePicker } from '@minetrace/ui/forms'
import {
  LuLeaf,
  LuRefreshCw,
  LuFileSpreadsheet,
  LuFileText,
  LuShieldCheck,
  LuCalculator,
  LuFactory,
  LuWeight,
  LuGauge,
  LuTruck,
  LuSettings,
  LuRoute,
  LuFuel,
} from 'react-icons/lu'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'

import { analyticsService } from '../../../api/services/analytics.service'
import type {
  SustainabilityReportResponse,
  SustainabilitySupplierRow,
  SustainabilityQuery,
  SustainabilityIncompleteReason,
  SustainabilityLotRow,
} from '../../../api/models/response/sustainability'
import { useHasPermission } from '../../../auth/PermissionContext'
import {
  ReportPage,
  ReportHeader,
  ActionButton,
  FilterBar,
  FilterField,
  VerdictBanner,
  KpiGrid,
  KpiTile,
  Panel,
  ToneBadge,
  Notice,
  EmptyState,
  ReportSkeleton,
} from '../../../components/analytics/ReportKit'
import { reportAccent, SERIES, chart, table, type Tone } from '../../../components/analytics/reportTheme'
import { fonts, mineral } from '../../../theme'

/* ─────────────────────────── Formato es-PE ─────────────────────────── */

function num(value: number | null | undefined, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return value.toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: digits })
}

// Emisiones en tCO2e: un viaje emite decenas de kg, así que van 3 decimales (1 kg de resolución).
// Siempre con 3 decimales fijos; un valor positivo menor a 1 kg se muestra como «< 0.001».
const tco2e = (v: number | null | undefined) => {
  if (v == null || !Number.isFinite(v)) return '—'
  if (v > 0 && v < 0.0005) return `< ${(0.001).toLocaleString('es-PE', { minimumFractionDigits: 3 })}`
  return v.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
}
// La misma cifra en kg (más legible en lotes pequeños).
const kgOf = (tons: number) => tons * 1000

// Referencia de orden de magnitud para leer la intensidad: camión diésel de ~35 TM,
// 8,5 km/gal, factor MINAM 10,21 kg CO2e/gal → 100 km ≈ 120 kg CO2e ≈ 3,4 kg CO2e/TM.
const REFERENCE_KG_PER_TM_PER_100KM = 3.4
// Km promedio por lote por debajo de los cuales la distancia es sospechosa (track GPS incompleto).
const SUSPICIOUS_KM_PER_LOT = 5

const SUPPLIER_LABEL_MAX = 26

/* ─────────────────────────── Página ─────────────────────────── */

export default function SostenibilidadPage() {
  const navigate = useNavigate()
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const [data, setData] = useState<SustainabilityReportResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<'pdf' | 'seal' | 'recalc' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)

  const canConfigure = useHasPermission('analytics.sustainability.configure')

  const query: SustainabilityQuery = useMemo(
    () => ({ dateFrom: dateFrom || undefined, dateTo: dateTo || undefined }),
    [dateFrom, dateTo],
  )

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await analyticsService.sustainability(query))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [query])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  // Los avisos de éxito se retiran solos; los errores quedan hasta la próxima acción.
  useEffect(() => {
    if (!msg) return
    const id = window.setTimeout(() => setMsg(null), 6000)
    return () => window.clearTimeout(id)
  }, [msg])

  const exportCsv = () => {
    if (!data) return
    const header = '#,Proveedor,Lotes,TotalTM,TotalEmisiones_tCO2e,IntensidadEmision_kgPorTM,ParticipacionPorc,LotesRechazados,EmisionesRechazadas_tCO2e'
    const rows = data.items.map((r) =>
      [
        r.rank, csv(r.supplierName), r.lots, r.totalTons, r.totalCo2eTons,
        r.intensityKgPerTon ?? '', r.participationPercent, r.rejectedLots, r.rejectedCo2eTons,
      ].join(','),
    )
    download(new Blob(['﻿' + [header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' }),
      `sostenibilidad-${data.generatedAt.slice(0, 10)}.csv`)
    setMsg('CSV descargado.')
  }

  const exportPdf = async () => {
    if (!data) return
    setError(null)
    setBusy('pdf')
    try {
      const url = analyticsService.sustainabilityPdfUrl(query)
      const token = localStorage.getItem('auth_token')
      const tenantId = localStorage.getItem('tenant_id')
      const res = await fetch(url, {
        headers: { Authorization: token ? `Bearer ${token}` : '', 'X-Tenant-Id': tenantId ?? '' },
      })
      if (!res.ok) throw new Error('No se pudo generar el PDF.')
      download(await res.blob(), `sostenibilidad-${data.generatedAt.slice(0, 10)}.pdf`)
      setMsg('PDF descargado.')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const saveSnapshot = async () => {
    setMsg(null)
    setError(null)
    setBusy('seal')
    try {
      const snap = await analyticsService.saveSustainabilitySnapshot(query)
      setMsg(`Reporte sellado (SHA-256 ${snap.contentSha256.slice(0, 12)}…).`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const recalculate = async () => {
    setMsg(null)
    setError(null)
    setBusy('recalc')
    try {
      const n = await analyticsService.recalculateSustainability(query)
      await fetchData()
      setMsg(n === 0
        ? 'No hay lotes pesados ni con huella previa en el período para recalcular.'
        : `Huella recalculada para ${n.toLocaleString('es-PE')} ${n === 1 ? 'lote' : 'lotes'}.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  // Intensidad global: Σ kg CO2e / Σ TM del mineral aceptado.
  const intensity = data && data.totalTons > 0 ? kgOf(data.totalCo2eTons) / data.totalTons : null
  const kmPerLot = data && data.totalLots > 0 && data.totalDistanceKm != null ? data.totalDistanceKm / data.totalLots : null

  return (
    <ReportPage>
      <ReportHeader
        icon={LuLeaf}
        title="Sostenibilidad"
        subtitle="Huella de carbono del transporte de mineral por proveedor: distancia × consumo del camión × factor de emisión MINAM. Emisiones, tonelaje, intensidad y participación (criterios ASG / LBMA)."
        actions={
          <>
            <ActionButton icon={LuRefreshCw} label="Refrescar" variant="ghost" loading={loading && !busy} onClick={() => void fetchData()} />
            <ActionButton icon={LuFileSpreadsheet} label="CSV" disabled={!data || data.items.length === 0} onClick={exportCsv} />
            <ActionButton icon={LuFileText} label="PDF" loading={busy === 'pdf'} disabled={!data} onClick={() => void exportPdf()} />
            {canConfigure && (
              <>
                <ActionButton icon={LuShieldCheck} label="Sellar" loading={busy === 'seal'} disabled={!data}
                  title="Guarda una copia cifrada del reporte con huella SHA-256" onClick={() => void saveSnapshot()} />
                <ActionButton icon={LuCalculator} label="Recalcular" variant="primary" loading={busy === 'recalc'}
                  title="Vuelve a calcular la huella de los lotes del período con los factores y la configuración vigentes"
                  onClick={() => void recalculate()} />
              </>
            )}
          </>
        }
      />

      <FilterBar note="Se filtra por fecha de despacho del traslado. Sin rango: todo el histórico con huella calculada.">
        <FilterField label="Periodo de despacho" width={{ base: '100%', md: '320px' }}>
          <Flex gap={2} align="center">
            <Box flex={1} minW={0}>
              <DatePicker value={dateFrom} onChange={setDateFrom} maxDate={dateTo || undefined} placeholder="Desde" />
            </Box>
            <Text fontSize="12px" color={mineral.ink4} flexShrink={0}>a</Text>
            <Box flex={1} minW={0}>
              <DatePicker value={dateTo} onChange={setDateTo} minDate={dateFrom || undefined} placeholder="Hasta" />
            </Box>
          </Flex>
        </FilterField>
      </FilterBar>

      <AnimatePresence>
        {error && (
          <motion.div key="err" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
            <Notice tone="bad">{error}</Notice>
          </motion.div>
        )}
        {msg && (
          <motion.div key="msg" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
            <Notice tone="ok">{msg}</Notice>
          </motion.div>
        )}
      </AnimatePresence>

      {loading && !data && <ReportSkeleton />}

      {data && (
        <motion.div key={data.generatedAt} initial={{ opacity: 0.4 }} animate={{ opacity: loading ? 0.55 : 1 }} transition={{ duration: 0.25 }}>
          <Flex direction="column" gap={5}>
            <Verdict data={data} intensity={intensity} kmPerLot={kmPerLot} />

            <KpiGrid>
              <KpiTile index={0} icon={LuFactory} label="Emisiones totales" value={data.totalLots > 0 ? data.totalCo2eTons : null}
                format={tco2e} unit="tCO2e" tone="accent"
                hint={data.totalLots > 0 ? `= ${num(kgOf(data.totalCo2eTons), 1)} kg CO2e del mineral aceptado` : 'Sin lotes con huella'} />
              <KpiTile index={1} icon={LuWeight} label="Tonelaje transportado" value={data.totalLots > 0 ? data.totalTons : null}
                format={(n) => num(n, 1)} unit="TM" hint="Peso neto de balanza de los lotes aceptados" />
              <KpiTile index={2} icon={LuGauge} label="Intensidad de emisión" value={intensity}
                format={(n) => num(n, 2)} unit="kg/TM" hint="kg de CO2e por tonelada de mineral" />
              <KpiTile index={3} icon={LuTruck} label="Lotes con huella" value={data.totalLots}
                format={(n) => num(n, 0)}
                tone={data.incompleteLots > data.totalLots ? 'warn' : undefined}
                hint={lotsHint(data)} />
            </KpiGrid>

            {data.items.length === 0 ? (
              <Panel>
                <EmptyState icon={LuLeaf} title="Sin huella de carbono en el período"
                  hint="La huella se calcula al cerrar el pesaje neto (o al rechazar el lote). Para lotes históricos, usa «Recalcular»." />
              </Panel>
            ) : (
              <>
                <Grid templateColumns={{ base: '1fr', xl: '3fr 2fr' }} gap={4}>
                  <Panel index={0} title="Emisiones por proveedor"
                    subtitle={`Top ${Math.min(10, data.items.length)} en kg CO2e del mineral aceptado`}>
                    <EmissionsBarChart items={data.items.slice(0, 10)} />
                  </Panel>
                  <Panel index={1} title="Participación en las emisiones" subtitle="Qué parte del total aporta cada proveedor">
                    <ParticipationBar items={data.items} />
                  </Panel>
                </Grid>

                <Grid templateColumns={{ base: '1fr', xl: data.incompleteLots > 0 ? '1fr 1fr' : '1fr' }} gap={4}>
                  <IntensityExplainer data={data} intensity={intensity} />
                  {data.incompleteLots > 0 && (
                    <MissingFootprintPanel data={data} onConfigure={() => navigate('/app/analitica/configuracion')} />
                  )}
                </Grid>

                <EmissionsTable items={data.items} avgIntensity={intensity} />
                {data.lots && data.lots.length > 0 && <LotsTable lots={data.lots} />}
              </>
            )}
          </Flex>
        </motion.div>
      )}
    </ReportPage>
  )
}

/* ─────────────────────────── Veredicto ─────────────────────────── */

function Verdict({ data, intensity, kmPerLot }: {
  data: SustainabilityReportResponse; intensity: number | null; kmPerLot: number | null
}) {
  const meta = intensity != null
    ? <><Text fontFamily={fonts.display} fontWeight={800} fontSize="20px" color={mineral.ink}>{num(intensity, 2)}</Text>kg CO2e / TM</>
    : undefined

  if (data.totalLots === 0) {
    return (
      <VerdictBanner tone="neutral" title="Aún no hay huella de carbono calculada en el período"
        subtitle={data.incompleteLots > 0
          ? `${data.incompleteLots} ${data.incompleteLots === 1 ? 'lote tiene' : 'lotes tienen'} el cálculo pendiente por datos faltantes: revisa el detalle más abajo.`
          : 'La huella se calcula al pesar el lote en planta.'} />
    )
  }
  if (kmPerLot != null && kmPerLot < SUSPICIOUS_KM_PER_LOT) {
    return (
      <VerdictBanner tone="warn" meta={meta}
        title={`Distancias muy cortas: ${num(kmPerLot, 1)} km por lote en promedio`}
        subtitle="Un traslado mina→planta rara vez baja de decenas de km. Probablemente el GPS del viaje quedó incompleto; usa «Recalcular» para estimar con la ruta mina→planta." />
    )
  }
  if (data.incompleteLots > data.totalLots) {
    return (
      <VerdictBanner tone="warn" meta={meta}
        title={`Solo ${data.totalLots} de ${data.totalLots + data.incompleteLots} lotes tienen huella`}
        subtitle="El total está subestimado: la mayoría de lotes no pudo calcularse por datos del vehículo. Revisa los motivos más abajo." />
    )
  }
  return (
    <VerdictBanner tone="ok" meta={meta}
      title={`${tco2e(data.totalCo2eTons)} tCO2e en ${data.totalLots} ${data.totalLots === 1 ? 'lote' : 'lotes'} de ${data.supplierCount} ${data.supplierCount === 1 ? 'proveedor' : 'proveedores'}`}
      subtitle={data.rejectedLots > 0
        ? `Además, ${tco2e(data.rejectedCo2eTons)} tCO2e de ${data.rejectedLots} ${data.rejectedLots === 1 ? 'lote rechazado' : 'lotes rechazados'} (el camión viajó, el mineral no se aceptó).`
        : 'Huella del transporte del mineral aceptado en planta.'} />
  )
}

function lotsHint(d: SustainabilityReportResponse): string {
  const parts: string[] = []
  if (d.gpsLots != null && d.estimatedLots != null && d.totalLots > 0) {
    parts.push(`${d.gpsLots} por GPS · ${d.estimatedLots} estimados`)
  }
  if (d.incompleteLots > 0) parts.push(`${d.incompleteLots} sin huella`)
  if (d.rejectedLots > 0) parts.push(`${d.rejectedLots} rechazados`)
  return parts.join(' · ') || 'Lotes aceptados con huella'
}

/* ─────────────────────────── Gráficos ─────────────────────────── */

// Barras horizontales: los nombres de proveedor (largos) se leen sin rotar.
// Animación de recharts apagada: dibuja con requestAnimationFrame y, si la pestaña está
// en segundo plano o se captura antes de terminar, las barras quedan en altura 0 (el
// gráfico "vacío" con ejes que se veía). La entrada la anima el Panel con framer-motion.
function EmissionsBarChart({ items }: { items: SustainabilitySupplierRow[] }) {
  const rows = items.map((r) => ({ name: r.supplierName, kg: kgOf(r.totalCo2eTons), lots: r.lots }))
  const height = Math.max(160, rows.length * 44 + 40)
  return (
    <Box h={`${height}px`}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 56, left: 4, bottom: 4 }} barCategoryGap="28%">
          <CartesianGrid {...chart.grid} vertical horizontal={false} />
          <XAxis type="number" {...chart.axis} tickFormatter={(v: number) => num(v, 1)} />
          <YAxis type="category" dataKey="name" {...chart.axis} width={210} interval={0}
            tickFormatter={(v: string) => (v.length > SUPPLIER_LABEL_MAX ? `${v.slice(0, SUPPLIER_LABEL_MAX - 1)}…` : v)} />
          <Tooltip {...chart.tooltip}
            formatter={(v: unknown) => [`${num(Number(v), 1)} kg CO2e`, 'Emisiones']} />
          <Bar dataKey="kg" radius={[0, 6, 6, 0]} isAnimationActive={false}
            label={{ position: 'right', fill: mineral.ink2, fontSize: 11, formatter: (v: unknown) => num(Number(v), 1) }}>
            {rows.map((_, i) => <Cell key={i} fill={i === 0 ? reportAccent.color : `${reportAccent.color}b3`} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  )
}

// Participación como una barra apilada al 100 % + leyenda con % y kg (más legible que una
// torta con muchas porciones finas). Los segmentos crecen con framer-motion al cargar.
function ParticipationBar({ items }: { items: SustainabilitySupplierRow[] }) {
  const top = items.slice(0, 8)
  const rest = items.slice(8)
  const segments = [
    ...top.map((r, i) => ({ key: r.supplierName + i, name: r.supplierName, pct: r.participationPercent, t: r.totalCo2eTons, color: SERIES[i % SERIES.length] ?? reportAccent.color })),
    ...(rest.length > 0
      ? [{
          key: 'otros', name: `Otros (${rest.length})`,
          pct: rest.reduce((s, r) => s + r.participationPercent, 0),
          t: rest.reduce((s, r) => s + r.totalCo2eTons, 0),
          color: mineral.ink4,
        }]
      : []),
  ].filter((s) => s.pct > 0)

  if (segments.length === 0) {
    return <Text fontSize="13px" color={mineral.ink3}>Sin emisiones del mineral aceptado para repartir.</Text>
  }

  return (
    <Flex direction="column" gap={4}>
      <Flex h="18px" borderRadius="9px" overflow="hidden" bg={mineral.sunk}>
        {segments.map((s, i) => (
          <motion.div key={s.key} title={`${s.name}: ${num(s.pct, 1)} %`}
            initial={{ width: 0 }} animate={{ width: `${s.pct}%` }}
            transition={{ delay: 0.05 * i, duration: 0.5, ease: 'easeOut' }}
            style={{ background: s.color, minWidth: s.pct > 0 ? 3 : 0, borderRight: '2px solid #fff' }} />
        ))}
      </Flex>
      <Flex direction="column" gap={2.5}>
        {segments.map((s) => (
          <Flex key={s.key} align="center" gap={2.5} fontSize="13px">
            <Box w="10px" h="10px" borderRadius="3px" bg={s.color} flexShrink={0} />
            <Text flex={1} minW={0} color={mineral.ink2} lineClamp={1} title={s.name}>{s.name}</Text>
            <Text color={mineral.ink3} fontSize="12px" flexShrink={0}>{num(kgOf(s.t), 1)} kg</Text>
            <Text fontFamily={fonts.mono} fontWeight={600} color={mineral.ink} w="58px" textAlign="right" flexShrink={0}>
              {num(s.pct, 1)} %
            </Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  )
}

/* ─────────────────────────── Explicación de la intensidad ─────────────────────────── */

function IntensityExplainer({ data, intensity }: { data: SustainabilityReportResponse; intensity: number | null }) {
  const steps: { icon: typeof LuRoute; label: string; value: string }[] = [
    { icon: LuRoute, label: 'Recorrido', value: data.totalDistanceKm != null ? `${num(data.totalDistanceKm, 1)} km` : '—' },
    { icon: LuFuel, label: 'Combustible', value: data.totalFuelGallons != null ? `${num(data.totalFuelGallons, 1)} gal` : '—' },
    { icon: LuFactory, label: 'Emisiones', value: `${num(kgOf(data.totalCo2eTons), 1)} kg CO2e` },
    { icon: LuWeight, label: 'Mineral', value: `${num(data.totalTons, 1)} TM` },
  ]
  return (
    <Panel index={2} title="Cómo leer la intensidad" subtitle="kg de CO2e que emitió el transporte por cada tonelada de mineral aceptada en planta">
      <Flex direction="column" gap={4}>
        <Grid templateColumns={{ base: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }} gap={2}>
          {steps.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 * i }}>
              <Box px={3} py={2.5} borderRadius="12px" bg={mineral.surfaceAlt} border={`1px solid ${mineral.line}`}>
                <Flex align="center" gap={1.5} color={mineral.ink3} fontSize="11.5px">
                  <s.icon size={13} /> {s.label}
                </Flex>
                <Text fontWeight={700} fontSize="14px" color={mineral.ink} mt={0.5}>{s.value}</Text>
              </Box>
            </motion.div>
          ))}
        </Grid>
        <Text fontSize="13px" color={mineral.ink2} lineHeight="1.6">
          Los kilómetros salen del GPS del viaje (o, si falta, de la ruta mina→planta). Los galones son
          km ÷ rendimiento del camión (km/gal), y las emisiones, galones × factor MINAM del combustible
          (diésel: 10,21 kg CO2e/gal). Si el camión no tiene rendimiento registrado se usa el de Configuración,
          y si no tiene combustible se asume diésel. Se cuenta el viaje de ida con carga, mina→planta.
          La intensidad divide esas emisiones entre las toneladas aceptadas:
          {intensity != null
            ? <> aquí, <b>{num(intensity, 2)} kg CO2e por TM</b>.</>
            : <> sin tonelaje aceptado no se puede calcular.</>}
        </Text>
        <Notice tone="info">
          Referencia: un camión diésel de ~35 TM que recorre 100 km emite unos 120 kg CO2e, es decir
          ≈ {num(REFERENCE_KG_PER_TM_PER_100KM, 1)} kg CO2e/TM. Una intensidad menor indica rutas más cortas o
          camiones más eficientes; una muy por debajo de 1 suele delatar trayectos GPS incompletos.
        </Notice>
      </Flex>
    </Panel>
  )
}

/* ─────────────────────────── Lotes sin huella ─────────────────────────── */

// Qué hacer según el motivo que guardó el motor (texto en español del backend).
function fixFor(reason: string): string {
  const r = reason.toLowerCase()
  if (r.includes('rendimiento')) return 'Registra el rendimiento (km/gal) del vehículo en Maestros o define un rendimiento por defecto en Configuración de Analítica.'
  if (r.includes('combustible')) return 'Asigna el tipo de combustible al vehículo en Maestros (el factor MINAM depende de él).'
  if (r.includes('no registra recorrido')) return 'El lote se despachó en la misma planta o el GPS no se movió: no hubo transporte que medir (típico de pruebas).'
  if (r.includes('trayecto') || r.includes('distancia')) return 'Sin GPS ni coordenadas de mina y planta: registra la garita o el polígono de la planta.'
  return 'Revisa los datos del vehículo y del traslado.'
}

function MissingFootprintPanel({ data, onConfigure }: { data: SustainabilityReportResponse; onConfigure: () => void }) {
  const reasons: SustainabilityIncompleteReason[] = data.incompleteReasons && data.incompleteReasons.length > 0
    ? data.incompleteReasons
    : [{ reason: 'Faltan datos del vehículo o del trayecto.', lots: data.incompleteLots }]
  return (
    <Panel index={3} title={`${data.incompleteLots} ${data.incompleteLots === 1 ? 'lote' : 'lotes'} sin huella`}
      subtitle="No entran al total hasta completar el dato y pulsar «Recalcular»"
      actions={<ActionButton icon={LuSettings} label="Configuración" variant="ghost" onClick={onConfigure} />}>
      <Flex direction="column" gap={3}>
        {reasons.map((r, i) => (
          <motion.div key={r.reason} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
            <Flex gap={3} align="flex-start">
              <Text fontFamily={fonts.display} fontWeight={800} fontSize="20px" color={TONE_WARN_DEEP} minW="34px" lineHeight="1.1">
                {r.lots}
              </Text>
              <Box minW={0}>
                <Text fontSize="13px" fontWeight={600} color={mineral.ink}>{r.reason}</Text>
                <Text fontSize="12px" color={mineral.ink3} mt={0.5}>{fixFor(r.reason)}</Text>
              </Box>
            </Flex>
          </motion.div>
        ))}
      </Flex>
    </Panel>
  )
}

const TONE_WARN_DEEP = mineral.oroDeep

/* ─────────────────────────── Tabla ─────────────────────────── */

function EmissionsTable({ items, avgIntensity }: { items: SustainabilitySupplierRow[]; avgIntensity: number | null }) {
  const right = { ...table.th, textAlign: 'right' as const }
  const tdR = { ...table.td, textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const }
  const intensityTone = (v: number | null | undefined): Tone | null => {
    if (v == null || avgIntensity == null || avgIntensity <= 0) return null
    if (v > avgIntensity * 1.5) return 'warn'
    if (v < avgIntensity * 0.5) return 'info'
    return null
  }
  return (
    <Panel index={4} title="Detalle por proveedor" subtitle="Emisiones e intensidad del mineral aceptado; las de lotes rechazados van aparte" padded={false}>
      <Box overflowX="auto">
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
          <thead>
            <tr>
              <th style={table.th}>#</th>
              <th style={table.th}>Proveedor</th>
              <th style={right}>Lotes</th>
              <th style={right}>Total TM</th>
              <th style={right}>Emisiones (tCO2e)</th>
              <th style={right}>Intensidad (kg/TM)</th>
              <th style={right}>% Part.</th>
              <th style={right}>Rechazadas (tCO2e)</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r, i) => {
              const tone = intensityTone(r.intensityKgPerTon)
              return (
                <motion.tr key={`${r.rank}-${r.supplierName}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i, 12) * 0.03 }}>
                  <td style={{ ...table.td, color: mineral.ink3, width: 40 }}>{r.rank}</td>
                  <td style={{ ...table.td, color: mineral.ink, fontWeight: 600, maxWidth: 340 }}>{r.supplierName}</td>
                  <td style={tdR}>{num(r.lots, 0)}</td>
                  <td style={tdR}>{num(r.totalTons, 1)}</td>
                  <td style={tdR}>
                    {tco2e(r.totalCo2eTons)}
                    <Text as="span" display="block" fontSize="11px" color={mineral.ink3}>{num(kgOf(r.totalCo2eTons), 1)} kg</Text>
                  </td>
                  <td style={tdR}>
                    <Flex justify="flex-end" align="center" gap={2}>
                      {tone && <ToneBadge tone={tone}>{tone === 'warn' ? 'Alta' : 'Baja'}</ToneBadge>}
                      {num(r.intensityKgPerTon, 2)}
                    </Flex>
                  </td>
                  <td style={tdR}>{num(r.participationPercent, 1)} %</td>
                  <td style={tdR}>{r.rejectedLots > 0 ? `${tco2e(r.rejectedCo2eTons)} (${r.rejectedLots})` : '—'}</td>
                </motion.tr>
              )
            })}
          </tbody>
        </table>
      </Box>
    </Panel>
  )
}

/* ─────────────────────────── Detalle por lote ─────────────────────────── */

const SOURCE_LABEL: Record<string, { label: string; tone: Tone; hint: string }> = {
  Gps: { label: 'GPS', tone: 'ok', hint: 'Trayecto real registrado por el teléfono del conductor' },
  RoadApi: { label: 'Ruta vial', tone: 'info', hint: 'Distancia por carretera mina→planta (Google Routes): el GPS faltaba o estaba incompleto' },
  UtmEstimate: { label: 'Estimada', tone: 'warn', hint: 'Línea recta mina→planta × factor de tortuosidad (sin GPS ni ruta vial)' },
}

// La base auditable del reporte: cada fila es un viaje con su distancia, de dónde salió,
// galones y emisiones. Cuadra con los totales: Σ kg de los lotes aceptados = emisiones totales.
function LotsTable({ lots }: { lots: SustainabilityLotRow[] }) {
  const right = { ...table.th, textAlign: 'right' as const }
  const tdR = { ...table.td, textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const }
  return (
    <Panel index={5} title="Detalle por lote" padded={false}
      subtitle="Cada viaje con su distancia, de dónde salió, consumo y emisiones. Los totales de arriba suman estas filas.">
      <Box overflowX="auto">
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1040 }}>
          <thead>
            <tr>
              <th style={table.th}>Lote</th>
              <th style={table.th}>Proveedor</th>
              <th style={table.th}>Despacho</th>
              <th style={table.th}>Distancia</th>
              <th style={right}>km</th>
              <th style={right}>km/gal</th>
              <th style={right}>Galones</th>
              <th style={right}>kg CO2e</th>
              <th style={right}>TM</th>
              <th style={right}>kg/TM</th>
            </tr>
          </thead>
          <tbody>
            {lots.map((l, i) => {
              const src = l.distanceSource ? SOURCE_LABEL[l.distanceSource] : null
              return (
                <motion.tr key={l.shipmentRecordId} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i, 15) * 0.02 }}
                  style={{ opacity: l.isComplete ? 1 : 0.75 }}>
                  <td style={{ ...table.td, fontFamily: fonts.mono, fontSize: 12, color: mineral.ink, whiteSpace: 'nowrap' }}>
                    {l.batchCode}
                    {l.vehiclePlate && <Text as="span" display="block" fontSize="11px" color={mineral.ink3}>{l.vehiclePlate}</Text>}
                  </td>
                  <td style={{ ...table.td, maxWidth: 240 }}>
                    <Text lineClamp={1} title={l.supplierName}>{l.supplierName}</Text>
                  </td>
                  <td style={{ ...table.td, whiteSpace: 'nowrap' }}>
                    {new Date(l.dispatchedAt.endsWith('Z') ? l.dispatchedAt : `${l.dispatchedAt}Z`).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  {l.isComplete ? (
                    <>
                      <td style={table.td}>
                        <Flex align="center" gap={2} wrap="wrap">
                          {src ? <Box title={src.hint}><ToneBadge tone={src.tone}>{src.label}</ToneBadge></Box> : '—'}
                          {l.rejected && <ToneBadge tone="bad">Rechazado</ToneBadge>}
                        </Flex>
                      </td>
                      <td style={tdR}>{num(l.distanceKm, 1)}</td>
                      <td style={tdR}>{num(l.performanceKmPerGal, 1)}</td>
                      <td style={tdR}>{num(l.fuelGallons, 1)}</td>
                      <td style={{ ...tdR, fontWeight: 600, color: mineral.ink }}>{num(l.co2eKg, 1)}</td>
                      <td style={tdR}>{num(l.netWeightTons, 1)}</td>
                      <td style={tdR}>{l.rejected ? '—' : num(l.intensityKgPerTon, 2)}</td>
                    </>
                  ) : (
                    <td colSpan={7} style={{ ...table.td, color: mineral.oroDeep }}>
                      Sin huella: {l.incompleteReason ?? 'faltan datos del vehículo o del trayecto.'}
                    </td>
                  )}
                </motion.tr>
              )
            })}
          </tbody>
        </table>
      </Box>
    </Panel>
  )
}

/* ─────────────────────────── Auxiliares ─────────────────────────── */

function download(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}

function csv(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return '"' + value.replace(/"/g, '""') + '"'
  }
  return value
}
